// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xGoogleAnalyticsState } from "@west/App/Store/Reducers";
import { mergeWithCss } from "@west/XDL/Utilities";
import { myAccountFlyoutLinkClickHandler } from "@west/XDL/Utilities/tracking";
import React, { FC } from "react";

interface MiniSignInProps {
    myAccountLinks?: PageLinkModel[];
    displayChangeCustomerLink?: boolean;
    onSignOutHandler?: () => void;
    extendedStyles?: MiniMyAccountStyles;
}

type Props = MiniSignInProps;

export interface MiniMyAccountStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    titleContainer?: InjectableCss;
    gridContainer?: GridContainerProps;
    linkGridItem?: GridItemProps;
    link?: LinkPresentationProps;
}

export const miniMyAccountStyles: MiniMyAccountStyles = {};

const MiniMyAccount: FC<Props> = ({ myAccountLinks, displayChangeCustomerLink, onSignOutHandler, extendedStyles }) => {
    const [styles] = React.useState(() => mergeWithCss(miniMyAccountStyles, extendedStyles));

    const { isPunchOutSession, name, xGoogleAnalytics } = useAppSelector(state => ({
        name: getSession(state).firstName || getSession(state).userName,
        isPunchOutSession: getIsPunchOutSession(state),
        xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
    }));
    const myFilteredAccountLinks = myAccountLinks?.filter(
        pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    );

    return (
        <StyledWrapper {...styles.container} data-test-selector="header-mini-myAccount">
            <StyledWrapper {...styles.titleContainer}>
                <Typography {...styles.title}>{`${translate("Hi")}, ${name}`}</Typography>
            </StyledWrapper>
            <GridContainer {...styles.gridContainer}>
                {/* XNG-Changes: added google analytics event on onClick */}
                {myFilteredAccountLinks?.map(
                    link =>
                        link?.url && (
                            <GridItem key={`${link.title}_${link.url}`} {...styles.linkGridItem}>
                                <Link
                                    href={link?.url}
                                    onClick={() =>
                                        myAccountFlyoutLinkClickHandler(xGoogleAnalytics, link?.url, link?.title)
                                    }
                                    {...styles.link}
                                >
                                    {link?.title}
                                </Link>
                            </GridItem>
                        ),
                )}
                <GridItem {...styles.linkGridItem}>
                    <Link
                        onClick={() => {
                            onSignOutHandler?.();
                            hideAll();
                        }}
                        aria-label={isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                    >
                        {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                    </Link>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

export default MiniMyAccount;
